"use client";

import { motion, useInView } from "framer-motion";
import { Users2, Zap, ShieldCheck } from "lucide-react";
import { useRef } from "react";
import Image from "next/image";
import { useLocale } from "next-intl";

import * as FaIcons from "react-icons/fa";
import * as MdIcons from "react-icons/md";
import * as AiIcons from "react-icons/ai";
import * as HiIcons from "react-icons/hi";
import * as IoIcons from "react-icons/io";
import * as Io2Icons from "react-icons/io5";
import * as BiIcons from "react-icons/bi";
import { useGetAboutUs } from "@/app/hooks/main-hooks";

export default function About() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const locale = useLocale();

  const allIcons = {
    ...FaIcons,
    ...MdIcons,
    ...AiIcons,
    ...HiIcons,
    ...IoIcons,
    ...Io2Icons,
    ...BiIcons,
  };

  const { data } = useGetAboutUs();
  const about = data?.data || {};

  return (
    <section
      id="about"
      className="relative overflow-hidden bg-[#ffffff] py-24 lg:py-32"
    >
      {/* Gold ambient glow */}
      <div className="pointer-events-none absolute top-0 right-0 h-128 w-lg -translate-y-1/2 translate-x-1/2 rounded-full bg-[#d59726]/10 blur-[140px]" />

      <div ref={ref} className="container mx-auto relative z-10 px-6">
        <div className="grid items-stretch gap-16 lg:grid-cols-2 lg:gap-24">
          {/* Left Image + Cards */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative min-h-125 lg:min-h-full"
          >
            <div className="relative h-full w-full group">
              {/* Image Box */}
              <div className="relative z-10 h-full w-full overflow-hidden rounded-[2.5rem] border border-gray-100 shadow-[0_30px_60px_-15px_rgba(0,12,96,0.1)] transition-all duration-700 group-hover:shadow-2xl">
                <Image
                  src="/about.jpg"
                  alt="LogicHub ERP Expertise"
                  fill
                  className="object-cover transition-transform duration-1000 group-hover:scale-105"
                  priority
                />
                {/* Overlay Subtle Gradient */}
                <div className="absolute inset-0 bg-linear-to-t from-main-color/20 to-transparent" />
              </div>

              {/* Floating Experience Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="absolute -top-6 -right-6 z-20 flex flex-col items-center justify-center rounded-3xl border border-gray-100 bg-white/90 p-6 backdrop-blur-xl shadow-xl md:p-8"
              >
                <div className="mb-2 rounded-2xl bg-[#00d4ff]/10 p-3 text-[#00d4ff]">
                  <Users2 size={28} />
                </div>
                <div className="text-2xl font-black text-main-color">
                  {locale === "en" ? "15+" : "15+"}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  {locale === "en" ? "Years Experience" : "خبرة سنوات"}
                </div>
              </motion.div>

              {/* Secure Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="absolute -bottom-6 -left-6 z-20 flex items-center gap-4 rounded-2xl border border-[#00d4ff]/20 bg-main-color p-5 shadow-2xl"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#00d4ff] text-main-color">
                  <ShieldCheck size={24} />
                </div>
                <div>
                  <div className="text-xs font-black uppercase tracking-widest text-[#00d4ff]">
                    {locale === "en" ? "Reliable" : "موثوق"}
                  </div>
                  <div className="text-sm font-bold text-white">
                    {locale === "en" ? "ZERO ERRORS" : "بدون أخطاء"}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Content */}
          <div className="flex flex-col space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
            >
              <h2 className="mb-6 text-4xl font-black leading-tight text-main-color md:text-5xl">
                {locale === "en"
                  ? "Beyond System Delivery"
                  : "ما بعد تسليم النظام"}
                : <br />
                <span className="bg-linear-to-r from-main-color to-[#d59726] bg-clip-text text-transparent">
                  {locale === "en"
                    ? "Operational Excellence"
                    : "التميز التشغيلي"}
                </span>
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              <p className="text-xl font-medium leading-relaxed text-[#000000]">
                {about?.intro?.[locale]}
              </p>

              <div className="space-y-4 rounded-2xl border-l-4 border-[#d59726] bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#d59726]">
                  <Zap size={16} />{" "}
                  {locale === "en" ? "Our Philosophy" : "فلسفتنا"}
                </div>
                <p className="leading-relaxed text-main-color">
                  {about?.philosophy?.[locale]}
                </p>
              </div>

              <div
                className="text-lg leading-relaxed text-main-color"
                dangerouslySetInnerHTML={{
                  __html: about?.detailedAbout?.[locale] || "",
                }}
              />
            </motion.div>

            {/* Features */}
            <div className="grid grid-cols-1 gap-4 pt-8 sm:grid-cols-2">
              {about?.features?.map((feature: any, index: number) => {
                const Icon =
                  allIcons[feature?.icon as keyof typeof allIcons] ||
                  FaIcons.FaRegQuestionCircle;

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                    className="flex items-center gap-4 rounded-xl border border-main-color/20 bg-white p-4 shadow-sm transition-shadow hover:shadow-lg"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#d59726]/15 text-[#d59726]">
                      <Icon size={20} className="text-[#d59726]" />
                    </div>
                    <span className="text-sm font-bold uppercase tracking-tight text-main-color">
                      {feature?.name?.[locale]}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
